import {
  IonContent,
  IonPage,
  IonButton,
  IonCard,
  IonCardContent,
} from '@ionic/react';
import { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { useWallet } from '../contexts/WalletContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { playCelebrationSound, playScratchingSound, stopScratchingSound } from '../utils/soundUtils';
import './ScratchCard.css';

// Possible prize values (same as spin wheel)
const PRIZE_VALUES = [5, 10, 25, 50, 100, 200];

const ScratchCard: React.FC = () => {
  const { earnCoins } = useWallet();
  const [prize, setPrize] = useState<number | null>(null);
  const [scratched, setScratched] = useState(false);
  const [canScratch, setCanScratch] = useState(true);
  const [lastScratchTime, setLastScratchTime] = useState<number | null>(null);
  const [scratchPercentage, setScratchPercentage] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const scratchingSoundRef = useRef<AudioBufferSourceNode | null>(null); // Reference to scratching sound

  // Confetti celebration function
  const triggerConfetti = () => {
    // Play celebration sound
    playCelebrationSound();
    
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: NodeJS.Timeout = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Launch confetti from both sides
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  // For testing: Set to 0 for unlimited scratches
  const SCRATCH_COOLDOWN = 0; // 0 = unlimited scratches for testing (change to 24 * 60 * 60 * 1000 for production)

  // Cleanup: Stop scratching sound if component unmounts while scratching
  useEffect(() => {
    return () => {
      if (scratchingSoundRef.current) {
        stopScratchingSound(scratchingSoundRef.current);
        scratchingSoundRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Check if user can scratch based on cooldown
    if (SCRATCH_COOLDOWN === 0) {
      setCanScratch(true);
      return;
    }
    
    const lastScratch = localStorage.getItem('lastScratchTime');
    if (lastScratch) {
      const timeSinceLastScratch = Date.now() - parseInt(lastScratch);
      if (timeSinceLastScratch < SCRATCH_COOLDOWN) {
        setCanScratch(false);
        setLastScratchTime(parseInt(lastScratch));
      } else {
        setCanScratch(true);
      }
    }
  }, []);

  useEffect(() => {
    // Generate a random prize when component mounts
    if (!prize) {
      const randomPrize = PRIZE_VALUES[Math.floor(Math.random() * PRIZE_VALUES.length)];
      setPrize(randomPrize);
    }
  }, [prize]);

  useEffect(() => {
    // Initialize canvas for scratching - wait for next frame to ensure layout is complete
    const initializeCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas size - use fixed dimensions or get from container
      const container = canvas.parentElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        // Use actual pixel dimensions for crisp rendering
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
        
        // Scale context for high DPI displays
        ctx.scale(dpr, dpr);
      } else {
        // Fallback to default size
        canvas.width = 350;
        canvas.height = 400;
      }

      // Only draw if canvas has valid dimensions
      if (canvas.width > 0 && canvas.height > 0) {
        // Fill with scratch surface (silver/metallic pattern)
        ctx.fillStyle = '#C0C0C0';
        ctx.fillRect(0, 0, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1));

        // Add pattern/texture
        ctx.fillStyle = '#A0A0A0';
        const width = canvas.width / (window.devicePixelRatio || 1);
        const height = canvas.height / (window.devicePixelRatio || 1);
        for (let i = 0; i < width; i += 20) {
          for (let j = 0; j < height; j += 20) {
            if ((i + j) % 40 === 0) {
              ctx.fillRect(i, j, 10, 10);
            }
          }
        }

        // Add text
        ctx.fillStyle = '#808080';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('SCRATCH HERE', width / 2, height / 2);
      }
    };

    // Use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      setTimeout(initializeCanvas, 100);
    });
  }, []);

  const getTimeRemaining = (): string => {
    if (!lastScratchTime || SCRATCH_COOLDOWN === 0) return '';
    const remaining = SCRATCH_COOLDOWN - (Date.now() - lastScratchTime);
    if (remaining <= 0) {
      setCanScratch(true);
      return '';
    }
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const resetCooldown = () => {
    localStorage.removeItem('lastScratchTime');
    setLastScratchTime(null);
    setCanScratch(true);
    resetCard();
  };

  const resetCard = () => {
    // Stop scratching sound if still playing
    if (scratchingSoundRef.current) {
      stopScratchingSound(scratchingSoundRef.current);
      scratchingSoundRef.current = null;
    }
    
    setScratched(false);
    setScratchPercentage(0);
    const randomPrize = PRIZE_VALUES[Math.floor(Math.random() * PRIZE_VALUES.length)];
    setPrize(randomPrize);
    
    // Reset canvas - use double requestAnimationFrame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Make sure canvas has dimensions
      if (canvas.width === 0 || canvas.height === 0) {
        const container = canvas.parentElement;
        if (container) {
          const rect = container.getBoundingClientRect();
          const dpr = window.devicePixelRatio || 1;
          canvas.width = rect.width * dpr;
          canvas.height = rect.height * dpr;
          canvas.style.width = `${rect.width}px`;
          canvas.style.height = `${rect.height}px`;
          ctx.scale(dpr, dpr);
        } else {
          canvas.width = 350;
          canvas.height = 400;
        }
      }

      // Get proper dimensions
      const container = canvas.parentElement;
      const rect = container ? container.getBoundingClientRect() : { width: 350, height: 400 };
      const dpr = window.devicePixelRatio || 1;
      
      // Reset canvas dimensions - always reset to ensure proper scaling
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(dpr, dpr);
      
      const width = rect.width;
      const height = rect.height;

      // Reset composite operation
      ctx.globalCompositeOperation = 'source-over';
      
      // Clear entire canvas
      ctx.clearRect(0, 0, width, height);
      
      // Redraw scratch surface (silver/metallic pattern)
      ctx.fillStyle = '#C0C0C0';
      ctx.fillRect(0, 0, width, height);

      // Add pattern/texture
      ctx.fillStyle = '#A0A0A0';
      for (let i = 0; i < width; i += 20) {
        for (let j = 0; j < height; j += 20) {
          if ((i + j) % 40 === 0) {
            ctx.fillRect(i, j, 10, 10);
          }
        }
      }

      // Add text
      ctx.fillStyle = '#808080';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('SCRATCH HERE', width / 2, height / 2);
      });
    });
  };

  const handleStart = (e: React.TouchEvent | React.MouseEvent) => {
    if (!canScratch || scratched) return;
    
    // Prevent default to avoid scrolling/zooming on mobile
    if ('touches' in e) {
      e.preventDefault();
    }
    
    isDrawingRef.current = true;
    
    // Start scratching sound when user starts scratching
    if (!scratchingSoundRef.current) {
      scratchingSoundRef.current = playScratchingSound();
    }
    
    handleMove(e);
  };

  const handleMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDrawingRef.current || !canScratch || scratched) return;

    // Prevent default to avoid scrolling on mobile
    if ('touches' in e) {
      e.preventDefault();
    }

    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x: number, y: number;

    if ('touches' in e && e.touches.length > 0) {
      // For touch events, get position relative to canvas element
      const touch = e.touches[0];
      // Get CSS pixel coordinates relative to canvas
      const cssX = touch.clientX - rect.left;
      const cssY = touch.clientY - rect.top;
      // Convert to logical canvas coordinates (accounting for context scaling)
      // Since ctx is scaled by dpr, we use CSS pixel coordinates directly
      x = cssX;
      y = cssY;
    } else if ('clientX' in e) {
      // For mouse events
      const cssX = e.clientX - rect.left;
      const cssY = e.clientY - rect.top;
      x = cssX;
      y = cssY;
    } else {
      return;
    }

    // Erase with a circular brush - use logical coordinates (context is already scaled)
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    // Brush size in logical pixels (context is scaled by dpr, so this is correct)
    // Increase this value to make the scratch pointer bigger (currently 30)
    ctx.arc(x, y, 30, 0, Math.PI * 2);
    ctx.fill();

    // Calculate scratched percentage (only if canvas has valid dimensions)
    if (canvas.width > 0 && canvas.height > 0) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      let transparentPixels = 0;
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] === 0) {
          transparentPixels++;
        }
      }
      const percentage = (transparentPixels / (pixels.length / 4)) * 100;
      setScratchPercentage(percentage);

      // If 80% or more is scratched, reveal the prize
      if (percentage >= 80 && !scratched && prize) {
        setScratched(true);
        
        // Trigger confetti celebration
        triggerConfetti();
        
        // Award coins
        earnCoins(prize, `Scratch & Win - Won ${prize} coins`, 'scratch-card')
          .catch((error) => {
            console.error('Error awarding coins:', error);
          });

        // Set cooldown (only if cooldown is enabled)
        if (SCRATCH_COOLDOWN > 0) {
          const now = Date.now();
          localStorage.setItem('lastScratchTime', now.toString());
          setLastScratchTime(now);
          setCanScratch(false);
        }
      }
    }
  };

  const handleEnd = () => {
    isDrawingRef.current = false;
    
    // Stop scratching sound when user stops scratching
    if (scratchingSoundRef.current) {
      stopScratchingSound(scratchingSoundRef.current);
      scratchingSoundRef.current = null;
    }
  };

  return (
    <IonPage>
      <AppHeader title="Scratch & Win" showBackButton={true} defaultHref="/games" />
      <IonContent fullscreen className="ion-padding">
        <div className="scratch-card-container">
          <IonCard>
            <IonCardContent>
              <h2 className="game-title">🎫 Scratch & Win</h2>
              <p className="game-description">
                Scratch the card to reveal your prize! Scratch at least 80% to win.
              </p>
            </IonCardContent>
          </IonCard>

          {/* Scratch Card */}
          <div className="scratch-card-wrapper">
            <div className="card-container">
              <div className="prize-display">
                {scratched && prize ? (
                  <div className="prize-revealed">
                    <div className="prize-amount">{prize}</div>
                    <div className="prize-label">COINS</div>
                  </div>
                ) : (
                  <div className="prize-hidden">
                    <div className="prize-icon">🎁</div>
                    <div className="prize-label">SCRATCH TO REVEAL</div>
                  </div>
                )}
              </div>
              <canvas
                ref={canvasRef}
                className="scratch-canvas"
                onMouseDown={handleStart}
                onMouseMove={handleMove}
                onMouseUp={handleEnd}
                onMouseLeave={handleEnd}
                onTouchStart={handleStart}
                onTouchMove={handleMove}
                onTouchEnd={handleEnd}
                style={{ cursor: 'crosshair' }}
              />
            </div>
            {!scratched && (
              <div className="scratch-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${Math.min(scratchPercentage, 80)}%` }}
                  />
                </div>
                <p className="progress-text">
                  {scratchPercentage < 80
                    ? `Scratch ${Math.round(80 - scratchPercentage)}% more to reveal!`
                    : 'Keep scratching to reveal!'}
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="scratch-controls">
            {canScratch ? (
              scratched ? (
                <IonButton
                  expand="block"
                  color="primary"
                  size="large"
                  onClick={resetCard}
                  className="new-card-button"
                >
                  Get New Card
                </IonButton>
              ) : (
                <IonCard>
                  <IonCardContent>
                    <p style={{ textAlign: 'center', margin: 0 }}>
                      👆 Touch and drag to scratch the card
                    </p>
                  </IonCardContent>
                </IonCard>
              )
            ) : (
              <IonCard>
                <IonCardContent>
                  <p style={{ textAlign: 'center', margin: 0 }}>
                    ⏰ Next card available in: <strong>{getTimeRemaining()}</strong>
                  </p>
                  <p style={{ textAlign: 'center', fontSize: '0.9rem', color: 'var(--ion-text-color-step-600)', marginTop: '0.5rem' }}>
                    You can scratch once every 24 hours
                  </p>
                  <IonButton
                    expand="block"
                    fill="clear"
                    size="small"
                    onClick={resetCooldown}
                    style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}
                  >
                    🔄 Reset Cooldown (Testing)
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}
          </div>

          {/* Prize Info */}
          <IonCard>
            <IonCardContent>
              <h3>Possible Prizes</h3>
              <div className="prize-list">
                {PRIZE_VALUES.map((value) => (
                  <div key={value} className="prize-item">
                    <span className="prize-value">{value} coins</span>
                  </div>
                ))}
              </div>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
      <BottomNav />
    </IonPage>
  );
};

export default ScratchCard;

